import type { SiteContent } from '../api/types';

/**
 * The page renders from this immediately, then swaps to whatever the API returns.
 * A marketing site that shows nothing because an API is down is worse than one
 * showing slightly stale copy, so this is the default rather than a spinner.
 *
 * Keep in step with `backend/src/content/site.ts`.
 */
export const fallbackContent: SiteContent = {
  company: {
    name: 'ArctiLoop',
    legalName: 'ArctiLoop OÜ',
    registryCode: '16425931',
    tagline: 'Backend, frontend and mobile apps, end to end',
    location: 'Tallinn, Estonia',
    phone: '+372 5835 6326',
    phoneHref: '+37258356326',
    officeHours: 'Weekdays 09:00–18:00 EET',
    availability: { open: true, label: 'Available for new work' },
  },

  hero: {
    eyebrow: 'Software studio · Tallinn, Estonia',
    headline: { lead: "The loop doesn't stop at ", accent: 'launch', trail: '.' },
    body: 'We build the backend, the interface and the mobile app — then keep going round. Ship it, watch it under real load, fix what the plan got wrong, ship again. Most of the value in a product shows up after the first release.',
    primaryAction: { label: 'Start a conversation', href: '#contact' },
    secondaryAction: { label: 'See our work', href: '#work' },
    loopCore: 'Operate',
    loopNote:
      'We run what we build — and what we learn there goes back into the next release.',
  },

  services: {
    eyebrow: 'What we do',
    title: 'Three practices, one team, no handover cliff.',
    body: 'The same people who design the data model write the screen that shows it and the app that carries it. Nothing gets lost being thrown over a wall.',
  },

  practices: [
    {
      id: 'backend',
      node: 'Backend',
      tag: 'Backend',
      title: 'The part nobody sees until it breaks',
      body: 'Distributed services, event pipelines, integrations with hardware and third-party APIs, and data models built to survive growth rather than the first demo.',
      note: 'Distributed services, event pipelines and the data underneath them.',
      tools: ['Java · Spring Boot', 'Node.js', 'Kafka', 'PostgreSQL', 'REST & WebSocket'],
    },
    {
      id: 'frontend',
      node: 'Frontend',
      tag: 'Frontend',
      title: 'Interfaces built for daily use',
      body: 'Operator portals, dashboards and admin tools where density, state and detail matter more than decoration — because someone lives in this screen for eight hours.',
      note: 'Portals and dashboards for people who use them all day, not once.',
      tools: ['React', 'TypeScript', 'Vite', 'Design systems'],
    },
    {
      id: 'mobile',
      node: 'Apps',
      tag: 'Mobile',
      title: 'One codebase, both stores',
      body: 'iOS and Android from a single React Native codebase — built, signed, released and looked after, including the parts of the store process nobody enjoys.',
      note: 'One React Native codebase, released to both stores and maintained.',
      tools: ['React Native', 'Expo', 'iOS · Android', 'Push & deep links'],
    },
  ],

  cycle: {
    eyebrow: 'How we work',
    title: 'Four stations, and then round again.',
    body: 'This is a cycle, not a plan with an end date. Each pass is short enough that being wrong is cheap.',
    stations: [
      {
        step: '01',
        name: 'Frame',
        title: 'Understand the domain first',
        body: 'We learn how the business actually works before writing code. Half the job is deciding what not to build.',
      },
      {
        step: '02',
        name: 'Build',
        title: 'Small, reviewed increments',
        body: 'You see the thing running every week, not at the end. No six-month silence followed by a surprise.',
      },
      {
        step: '03',
        name: 'Ship',
        title: 'Into production early',
        body: 'Behind a flag if it needs to be. Real users and real data find problems no plan anticipates.',
      },
      {
        step: '04',
        name: 'Operate',
        title: 'Metrics, logs, alerts',
        body: 'We run what we build and watch it. What breaks at 3am tells you more than any requirements document.',
      },
    ],
    returnLabel: 'Back to Frame',
    cadence: 'Every two weeks',
  },

  work: {
    eyebrow: 'Selected work',
    title: 'A platform we built, shipped and still run.',
  },

  caseStudies: [
    {
      id: 'chargersavvy',
      title: 'ChargerSavvy — an EV charging platform, end to end',
      body: 'Charge point operators use it to run their sites; drivers use it to find a charger, charge and pay. We built every layer: the gateway that speaks OCPP to the physical chargers, the services behind it, the operator portal, and the driver app on both stores. Then we kept running it.',
      url: 'https://chargersavvy.io',
      urlLabel: 'chargersavvy.io',
      facts: [
        { label: 'Backend services', value: '10' },
        { label: 'Charger protocol', value: 'OCPP 1.6' },
        { label: 'Clients', value: 'Web portal · iOS · Android' },
        { label: 'Smart charging', value: 'Day-ahead market prices' },
        { label: 'Payments', value: 'Stripe' },
      ],
      tools: [
        'Java · Spring Boot',
        'Kafka',
        'PostgreSQL',
        'Redis',
        'React · TypeScript',
        'React Native',
        'Docker',
        'Prometheus · Grafana',
      ],
    },
  ],

  stack: {
    eyebrow: 'Tools',
    title: 'What we reach for.',
    body: 'Boring, well-understood technology, chosen because it will still be maintainable in five years.',
  },

  stackGroups: [
    {
      name: 'Backend',
      items: ['Java · Spring Boot', 'Node.js · Express', 'Apache Kafka', 'PostgreSQL', 'Redis'],
    },
    {
      name: 'Frontend',
      items: ['React', 'TypeScript', 'Vite', 'TanStack Query', 'Keycloak / OIDC'],
    },
    {
      name: 'Mobile',
      items: ['React Native', 'Expo', 'App Store · Play', 'Push notifications', 'Stripe'],
    },
    {
      name: 'Run it',
      items: ['Docker', 'Prometheus', 'Grafana · Loki', 'Alerting', 'CI pipelines'],
    },
  ],

  contact: {
    eyebrow: 'Contact',
    title: "Tell us what's broken, or what's missing.",
    body: "Write in English. A short description of the problem is enough to start — we'll come back with questions, not a quote template. First reply within one working day.",
    formIntro: 'Or send it straight from here.',
  },
};
